import { faEllipsis } from '@fortawesome/free-solid-svg-icons/faEllipsis';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons/faEllipsisVertical';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FunctionComponent, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ClickAwayListener from 'react-click-away-listener';
import { ActionsDropdownOption } from './ActionsDropdownOption';
import styles from './ActionsDropdown.module.scss';

interface ActionsDropdownProps {
    options: Array<ActionsDropdownOption>;
    direction: 'up' | 'down';
    className?: string;
}

export const ActionsDropdown: FunctionComponent<ActionsDropdownProps> = props => {
    const [expanded, setExpanded] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const calculatePosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownHeight = 150; // Approximate height

            let top = 0;
            if (props.direction === 'down') {
                top = rect.bottom + 8;
            } else {
                top = rect.top - dropdownHeight + 50;
            }

            const left = rect.right - 170; // Align to the right, dropdown width ~200px

            setPosition({ top, left });
        }
    };

    useEffect(() => {
        if (expanded) {
            calculatePosition();

            const handleScroll = () => calculatePosition();
            const handleResize = () => calculatePosition();

            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [expanded, props.direction]);

    const handleOptionClick = (option: ActionsDropdownOption) => {
        option.onClick();
        setExpanded(false);
    };

    return (
        <>
            <div
                ref={triggerRef}
                className={`${styles.container} ${props.className || ''}`}
                onClick={() => setExpanded(prev => !prev)}
            >
                <FontAwesomeIcon icon={expanded ? faEllipsisVertical : faEllipsis} />
            </div>
            {expanded &&
                createPortal(
                    <ClickAwayListener onClickAway={() => setExpanded(false)}>
                        <div
                            className={styles.dropdown}
                            style={{
                                position: 'fixed',
                                top: `${position.top}px`,
                                left: `${position.left}px`,
                                zIndex: 10000
                            }}
                        >
                            {props.options.map(option => (
                                <div
                                    key={option.id}
                                    className={`${styles.item} ${option.disabled ? styles.disabled : ''}`}
                                    onClick={() => !option.disabled && handleOptionClick(option)}
                                >
                                    {option.icon && (
                                        <FontAwesomeIcon
                                            icon={option.icon}
                                            className={styles.icon}
                                        />
                                    )}
                                    <span className={styles.label}>{option.label}</span>
                                </div>
                            ))}
                        </div>
                    </ClickAwayListener>,
                    document.body
                )}
        </>
    );
};

export default ActionsDropdown;
